import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  arrayUnion,
  arrayRemove,
} from '@firebase/firestore';
import { db } from './firebase';
import type { Review, ReviewCreateInput, ReviewStatus } from '../types';
import { updatePlaceRating } from './placesService';

const FLAG_THRESHOLD = 3; // Auto-flag after 3 reports

const COLLECTION_NAME = 'reviews';

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
function removeUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  ) as Partial<T>;
}

/**
 * Add a new review
 */
export async function addReview(review: ReviewCreateInput): Promise<string> {
  // Remove undefined values - Firestore doesn't accept undefined
  const cleanReview = removeUndefined(review);

  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...cleanReview,
    createdAt: serverTimestamp(),
    helpful: 0,
    helpfulVoters: [],
    status: 'approved', // New reviews are approved by default
    flagCount: 0,
    flaggedBy: [],
  });

  // Update place rating
  await recalculatePlaceRating(review.placeId);

  return docRef.id;
}

/**
 * Get all reviews for a place
 */
export async function getReviewsForPlace(placeId: string): Promise<Review[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Review[];
}

/**
 * Get a review by ID
 */
export async function getReviewById(id: string): Promise<Review | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Review;
}

/**
 * Update a review
 */
export async function updateReview(
  id: string,
  updates: Partial<Pick<Review, 'rating' | 'text' | 'visitDate'>>
): Promise<void> {
  const review = await getReviewById(id);
  if (!review) return;

  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  // Recalculate place rating if rating changed
  if (updates.rating !== undefined) {
    await recalculatePlaceRating(review.placeId);
  }
}

/**
 * Mark a review as helpful
 */
export async function markReviewHelpful(
  reviewId: string,
  userId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, reviewId);
  const review = await getReviewById(reviewId);

  if (!review) return;

  const isAlreadyHelpful = review.helpfulVoters.includes(userId);

  if (isAlreadyHelpful) {
    // Remove helpful vote
    await updateDoc(docRef, {
      helpful: review.helpful - 1,
      helpfulVoters: arrayRemove(userId),
    });
  } else {
    // Add helpful vote
    await updateDoc(docRef, {
      helpful: review.helpful + 1,
      helpfulVoters: arrayUnion(userId),
    });
  }
}

/**
 * Recalculate and update place rating (excludes hidden reviews)
 */
async function recalculatePlaceRating(placeId: string): Promise<void> {
  const allReviews = await getReviewsForPlace(placeId);

  // Only count visible reviews (not hidden)
  const visibleReviews = allReviews.filter((r) => r.status !== 'hidden');

  if (visibleReviews.length === 0) {
    await updatePlaceRating(placeId, 0, 0);
    return;
  }

  const totalRating = visibleReviews.reduce((sum, review) => sum + review.rating, 0);
  const averageRating = totalRating / visibleReviews.length;

  await updatePlaceRating(placeId, Math.round(averageRating * 10) / 10, visibleReviews.length);
}

/**
 * Subscribe to reviews for a place (real-time)
 */
export function subscribeToReviews(
  placeId: string,
  callback: (reviews: Review[]) => void
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const reviews = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Review[];
    callback(reviews);
  });
}

/**
 * Check if a user has already reviewed a place
 */
export async function hasUserReviewed(
  placeId: string,
  userId: string
): Promise<boolean> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId),
    where('userId', '==', userId)
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Get user's review for a place
 */
export async function getUserReviewForPlace(
  placeId: string,
  userId: string
): Promise<Review | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId),
    where('userId', '==', userId)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Review;
}

// ============================================
// MODERATION FUNCTIONS
// ============================================

/**
 * Flag a review (user reporting)
 */
export async function flagReview(
  reviewId: string,
  userId: string
): Promise<void> {
  const review = await getReviewById(reviewId);
  if (!review) return;

  // Check if user already flagged
  const flaggedBy = review.flaggedBy || [];
  if (flaggedBy.includes(userId)) {
    return; // Already flagged by this user
  }

  const newFlagCount = (review.flagCount || 0) + 1;
  const newFlaggedBy = [...flaggedBy, userId];

  // Auto-flag if threshold reached
  const newStatus: ReviewStatus = newFlagCount >= FLAG_THRESHOLD ? 'flagged' : (review.status || 'approved');

  const docRef = doc(db, COLLECTION_NAME, reviewId);
  await updateDoc(docRef, {
    flagCount: newFlagCount,
    flaggedBy: newFlaggedBy,
    status: newStatus,
  });
}

/**
 * Unflag a review (user removing their flag)
 */
export async function unflagReview(
  reviewId: string,
  userId: string
): Promise<void> {
  const review = await getReviewById(reviewId);
  if (!review) return;

  const flaggedBy = review.flaggedBy || [];
  if (!flaggedBy.includes(userId)) {
    return; // Not flagged by this user
  }

  const newFlagCount = Math.max(0, (review.flagCount || 0) - 1);

  const docRef = doc(db, COLLECTION_NAME, reviewId);
  await updateDoc(docRef, {
    flagCount: newFlagCount,
    flaggedBy: arrayRemove(userId),
  });
}

/**
 * Moderate a review (admin action)
 */
export async function moderateReview(
  reviewId: string,
  moderatorId: string,
  action: 'approve' | 'hide' | 'delete',
  note?: string
): Promise<void> {
  const review = await getReviewById(reviewId);
  if (!review) return;

  if (action === 'delete') {
    await deleteReview(reviewId);
    await recalculatePlaceRating(review.placeId);
    return;
  }

  const newStatus: ReviewStatus = action === 'approve' ? 'approved' : 'hidden';

  const docRef = doc(db, COLLECTION_NAME, reviewId);
  await updateDoc(docRef, {
    status: newStatus,
    moderatorNote: note || null,
    moderatedAt: serverTimestamp(),
    moderatedBy: moderatorId,
    // Reset flags if approved
    ...(action === 'approve' ? { flagCount: 0, flaggedBy: [] } : {}),
  });

  // Recalculate place rating (hidden reviews should be excluded)
  await recalculatePlaceRating(review.placeId);
}

/**
 * Delete a review
 */
export async function deleteReview(reviewId: string): Promise<void> {
  const review = await getReviewById(reviewId);
  if (!review) return;

  const docRef = doc(db, COLLECTION_NAME, reviewId);
  await deleteDoc(docRef);

  // Recalculate place rating
  await recalculatePlaceRating(review.placeId);
}

/**
 * Get reviews pending moderation (flagged or pending)
 */
export async function getReviewsForModeration(): Promise<Review[]> {
  // Get flagged reviews
  const flaggedQuery = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'flagged'),
    orderBy('createdAt', 'desc')
  );

  const flaggedSnapshot = await getDocs(flaggedQuery);
  const flaggedReviews = flaggedSnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as Review[];

  // Also get reviews with high flag count (but not yet auto-flagged)
  const highFlagQuery = query(
    collection(db, COLLECTION_NAME),
    where('flagCount', '>=', 1),
    orderBy('flagCount', 'desc')
  );

  const highFlagSnapshot = await getDocs(highFlagQuery);
  const highFlagReviews = (highFlagSnapshot.docs
    .map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as Review[])
    .filter((r) => r.status !== 'flagged' && r.status !== 'hidden');

  // Combine and deduplicate
  const allReviews = [...flaggedReviews];
  highFlagReviews.forEach((r) => {
    if (!allReviews.find((fr) => fr.id === r.id)) {
      allReviews.push(r);
    }
  });

  return allReviews;
}

/**
 * Get all reviews for admin (including hidden)
 */
export async function getAllReviewsForAdmin(): Promise<Review[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as Review[];
}

/**
 * Get visible reviews for a place (excluding hidden)
 */
export async function getVisibleReviewsForPlace(placeId: string): Promise<Review[]> {
  const reviews = await getReviewsForPlace(placeId);
  return reviews.filter((r) => r.status !== 'hidden');
}
